import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Package, Wrench, User, Heart, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import { CATEGORY_COLORS } from "./marketData";
import { PROFESSION_ICONS } from "./ProfessionCard";

const SECTION_ICONS = {
  routine: Clock,
  deliverables: Package,
  tools: Wrench,
  profile: User,
  emotional: Heart,
};

export default function ProfessionDetail({ profession, onBack, onSimulate }) {
  const [openSection, setOpenSection] = useState("routine");
  const colors = CATEGORY_COLORS[profession.category] || { bg: "#F2F4F4", text: "#646C6F", dot: "#99A6AB" };

  const toggle = (key) => setOpenSection(prev => prev === key ? null : key);

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      className="space-y-4"
    >
      {/* Back + Header */}
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: "#646C6F" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar ao mapa
      </button>

      <div className="bg-white rounded-2xl p-5" style={{ border: '1px solid #E5E9EA' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#EAFAF6", boxShadow: "0 0 12px rgba(0, 220, 164, 0.3)" }}>
            {(() => { const Icon = PROFESSION_ICONS[profession.id]; return Icon ? <Icon className="w-7 h-7" style={{ color: "#00664C" }} /> : null; })()}
          </div>
          <div className="flex-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mb-2"
              style={{ background: colors.bg, color: colors.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors.dot }} />
              {profession.category}
            </span>
            <h1 className="text-xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>{profession.name}</h1>
            <p className="text-sm mt-1" style={{ color: "#646C6F" }}>{profession.tagline}</p>
          </div>
        </div>

        <button
          onClick={() => onSimulate(profession)}
          className="mt-4 w-full py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
          style={{ background: "#00DCA4", color: "#003326" }}
        >
          <TrendingUp className="w-4 h-4" /> Ver médias de remuneração
        </button>
      </div>

      {/* Sections */}
      <Accordion
        id="routine"
        open={openSection === "routine"}
        onToggle={toggle}
        icon={Clock}
        title="Exemplo de Rotina Semanal"
      >
        <div className="space-y-3">
          <div className="space-y-2">
            {profession.weekRoutine.map((day, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-xs font-bold w-8 flex-shrink-0 mt-0.5" style={{ color: "#00664C" }}>{day.day}</span>
                <div className="flex flex-wrap gap-1">
                  {day.tasks.map((t, j) => (
                    <span key={j} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#EAFAF6", color: "#00664C" }}>{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4" style={{ borderTop: '1px solid #E5E9EA' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: "#003326" }}>Distribuição de tempo</p>
            <div className="space-y-2">
              {profession.timeDistribution.map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: "#646C6F" }}>{item.label}</span>
                    <span className="font-semibold" style={{ color: "#00664C" }}>{item.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "#E5E9EA" }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: "#00DCA4" }}
                      initial={{ width: 0 }}
                      animate={{ width: `${item.pct}%` }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Accordion>

      <Accordion id="deliverables" open={openSection === "deliverables"} onToggle={toggle} icon={Package} title="Entregáveis reais">
        <div className="flex flex-wrap gap-2">
          {profession.deliverables.map((d, i) => (
            <span key={i} className="text-sm px-3 py-1.5 rounded-full font-medium" style={{ background: "#F2F4F4", color: "#2E3233" }}>
              {d}
            </span>
          ))}
        </div>
      </Accordion>

      <Accordion id="tools" open={openSection === "tools"} onToggle={toggle} icon={Wrench} title="Ferramentas utilizadas">
        <div className="flex flex-wrap gap-2">
          {profession.tools.map((t, i) => (
            <span key={i} className="text-sm px-3 py-1.5 rounded-full font-medium" style={{ background: "#fff", color: "#003326", border: '1px solid #E5E9EA' }}>
              {t}
            </span>
          ))}
        </div>
      </Accordion>

      <Accordion id="profile" open={openSection === "profile"} onToggle={toggle} icon={User} title="Perfil que tende a dar certo">
        <ul className="space-y-2">
          {profession.successProfile.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "#2E3233" }}>
              <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "#00DCA4" }} />
              {p}
            </li>
          ))}
        </ul>
      </Accordion>

      <Accordion id="emotional" open={openSection === "emotional"} onToggle={toggle} icon={Heart} title="Realidade emocional">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "#FEEDB4" }}>
            <span className="text-sm font-semibold" style={{ color: "#65541B" }}>Nível de pressão:</span>
            <span className="text-sm font-medium" style={{ color: "#65541B" }}>{profession.emotionalReality.pressure}</span>
          </div>
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: "#646C6F" }}>Dores comuns</p>
            <ul className="space-y-1.5">
              {profession.emotionalReality.pains.map((p, i) => (
                <li key={i} className="text-sm flex items-start gap-2" style={{ color: "#2E3233" }}>
                  <span style={{ color: "#FF9D66" }}>•</span> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>
    </motion.div>
  );
}

function Accordion({ id, open, onToggle, icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E5E9EA' }}>
      <button
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4" style={{ color: "#00DCA4" }} />
          <span className="text-sm font-semibold" style={{ color: "#003326" }}>{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: "#99A6AB" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "#99A6AB" }} />}
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="px-5 pb-5"
        >
          {children}
        </motion.div>
      )}
    </div>
  );
}
